import { supabase } from "@/integrations/supabase/client";
import { VideoData, Comment, CommentTrend, APIError } from "@/types/youtube";
import { DatabaseService } from "./database";
import { YouTubeAPI } from "./youtubeAPI";

// Initialize services
const db = new DatabaseService(supabase);

/**
 * Fetch and process video data from YouTube
 * @param videoId The YouTube video ID to fetch
 * @returns Promise with the video data
 * @throws APIError if any operation fails
 */
export const fetchVideoData = async (videoId: string): Promise<VideoData> => {
  try {
    // Get video data and comments from Edge Function
    const { data: functionData, error: functionError } = await supabase.functions.invoke(
      "fetch-youtube-data",
      {
        body: { videoId },
      }
    );

    if (functionError) {
      console.error('Edge Function error:', functionError);
      
      // Extract error details from the response if available
      let errorMessage = functionError.message;
      let errorCode = 'UNKNOWN_ERROR';
      
      try {
        // Try to parse the error message as JSON
        const errorResponse = JSON.parse(functionError.message);
        if (errorResponse.error) {
          errorMessage = errorResponse.error;
          errorCode = errorResponse.code || errorCode;
        }
      } catch (e) {
        // If parsing fails, use the original error message
        console.warn('Could not parse error response:', e);
      }
      
      // Handle specific error codes
      switch (errorCode) {
        case 'NOT_FOUND':
          throw new Error(`Video not found: ${videoId}`);
        case 'AUTH_ERROR':
          throw new Error('YouTube API authorization failed. Please check your API key configuration.');
        case 'QUOTA_EXCEEDED':
          throw new Error('YouTube API quota exceeded. Please try again later.');
        case 'ACCESS_DENIED':
          throw new Error('Access to this video is restricted or it may be private.');
        case 'COMMENTS_DISABLED':
          // Don't throw an error for disabled comments, but include it in the response
          return {
            ...functionData,
            warning: 'Comments are disabled for this video',
            commentCount: 0
          };
        default:
          throw new Error(errorMessage || 'An unexpected error occurred');
      }
    }

    if (!functionData) {
      throw new Error('No data received from YouTube API');
    }

    // Store video data
    await db.upsertVideo(functionData);

    // Analyze comments with proper error handling
    try {
      await db.analyzeComments(videoId);
    } catch (error) {
      console.error("Error analyzing comments:", error);
      // We'll propagate this error to notify the user, but don't reject the whole promise
      // instead, append the error to the video data to show appropriate UI
      functionData.analysisError = error instanceof Error ? error.message : 'Unknown error during comment analysis';
    }

    return functionData;
  } catch (error) {
    const apiError = error as APIError;
    console.error("Error fetching video data:", apiError);
    throw apiError;
  }
};

/**
 * Fetch all stored videos
 * @returns Promise with array of video data
 * @throws APIError if the operation fails
 */
export const fetchAllVideos = async (): Promise<VideoData[]> => {
  return db.getAllVideos();
};

/**
 * Get comment trends for a video
 * @param videoId The video ID to get trends for
 * @returns Promise with array of comment trends
 * @throws APIError if the operation fails
 */
export const getVideoTrends = async (videoId: string): Promise<CommentTrend[]> => {
  return db.getVideoTrends(videoId);
};
